import {EventEmitter, Injectable} from "@angular/core";
import {interval, lastValueFrom, Observable, of, switchMap} from "rxjs";
import * as _ from 'lodash';
import {LocationInfo} from "../model/location.model";
import {CommonService} from "./common.service";
import {AlertController, Platform} from "@ionic/angular";
import {Geolocation} from '@capacitor/geolocation';
import {Position} from "@capacitor/geolocation/dist/esm/definitions";
import {Place} from "../model/place.model";
import {Map} from 'leaflet';
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private _userLocation: LocationInfo;

  map: Map;
  changeGeoPermission = new EventEmitter<boolean>();

  constructor(private commonSrv: CommonService,
              private alertController: AlertController,
              private translateService: TranslateService,
              private platForm: Platform) {
  }

  readUserLocation(): Observable<LocationInfo> {

    localStorage.setItem('position_requested_first', '1');

    if (this.platForm.platforms().indexOf('mobileweb') != -1) {
      return this._readWeb();
    } else {
      return this._readNative()
    }

  }

  changePermissions(isActive: boolean) {

    const permission = isActive ? 'granted' : 'denied';
    if (permission != localStorage.getItem('location_permission')) {
      this.changeGeoPermission.emit(isActive);
    }

    localStorage.setItem('location_permission', permission);
  }

  checkPermissions(): Promise<boolean> {
    return new Promise((resolve, reject) => {

      Geolocation.checkPermissions().then((res) => {
        resolve(res?.location == "granted");
      }, (error) => {
        reject(error);
      });

    });
  }

  async onOpenPositionPermission() {

    if (this.commonSrv.isMobile()) {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App
      })
    } else {

      this.readUserLocation().subscribe((res) => {
      }, async (error) => {
        if (error?.code == 1) {
          const alert = await this.alertController.create({
            header: this.translateService.instant('Attenzione'),
            subHeader: this.translateService.instant('Geolocalizzazione_bloccata'),
            message: this.translateService.instant('Per_usare_questa_funzionalità'),
            buttons: ['OK']
          });

          await alert.present();
        }
      });
    }


  }

  setUserLocation(value: LocationInfo) {
    this._userLocation = value;
  }

  getUserLocation(): LocationInfo {
    return this._userLocation;
  }

  private _readWeb(): Observable<LocationInfo> {
    return new Observable((observer) => {
      navigator.geolocation.getCurrentPosition((res) => {

        var _userLocation: LocationInfo = {
          lat: _.get(res, 'coords.latitude'),
          lng: _.get(res, 'coords.longitude'),
        };

        this.setUserLocation(_userLocation);
        observer.next(_userLocation);
        observer.complete();
      }, async (err) => {
        console.error(err);
        observer.next({});
        observer.complete();
      }, {timeout: 10000, maximumAge: 0, enableHighAccuracy: true});
    });
  }

  public static calcolaTempoAPiedi(distanzaKm) {
    const minutiPerKm = 12; // tempo medio in minuti per chilometro
    const value = distanzaKm * minutiPerKm;
    return value.toFixed(0) + " m";
  }

  static getDistanceFromLabel(lat1, lon1, lat2, lon2) {

    let value = this.getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2);
    const isKm = value >= 1000;
    const convertedValue = isKm ? value / 1000 : value;
    return convertedValue.toFixed(0) + (isKm ? ' km' : ' m');
  }

  static getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {

    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2 - lat1);  // deg2rad below
    var dLon = deg2rad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    ;
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in km
    return d * 1000;

    function deg2rad(deg) {
      return deg * (Math.PI / 180);
    }
  }

  private _readNative(): Observable<LocationInfo> {
    return new Observable<LocationInfo>((observer) => {

      const finishEmpty = () => {
        observer.next({} as LocationInfo); // valore uniforme, mai undefined
        observer.complete();
      };

      (async () => {
        try {
          let status = await Geolocation.checkPermissions();

          // stato "da decidere" → mostra il dialog di sistema
          if (status?.location === 'prompt' || status?.location === 'prompt-with-rationale') {
            status = await Geolocation.requestPermissions();
          }

          // se non concesso (negato, bloccato o solo coarse mancante) → prosegui senza posizione
          if (status?.location !== 'granted' && status?.coarseLocation !== 'granted') {
            finishEmpty();
            return;
          }

          const res = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false, // rete invece del GPS: più veloce, meno hang
            timeout: 8000,             // non resta appeso all'infinito
            maximumAge: 60000,         // accetta una posizione recente in cache
          });

          const _userLocation: LocationInfo = {
            lat: _.get(res, 'coords.latitude'),
            lng: _.get(res, 'coords.longitude'),
          };

          this.setUserLocation(_userLocation);
          observer.next(_userLocation);
          observer.complete();

        } catch (e) {
          // qualsiasi errore (timeout, permesso, eccezione) → prosegui senza posizione
          console.error('[_readNative] Errore lettura posizione', e);
          finishEmpty(); // mai observer.error(): non rompere il flusso RxJS
        }
      })();
    });
  }

}
